import { useEffect, useRef, useCallback, useState } from "react";
import { useToast } from "@repo/ui";
import {
  ALL_TASKS_BRAND_PREFIX,
  ALL_TASKS_PREFIX,
} from "~/pages/dashboard/task/tab-windows/utils/window-helpers";
import type { Task } from "~/types/api";
import { tabWindowStorage } from "~/utils/tab-window-storage";
import { buildWindowData } from "./build-window-data";
import type { TabWindow, TabWindowState } from "../../../task.types";
import { useWindowDataLoader } from "../apis/use-window-data-loader";

type Props<T> = {
  tasks: Task[] | undefined;
  setWindows: React.Dispatch<React.SetStateAction<TabWindow<T>[]>>;
};

export const useWindowInitializer = <T>({ tasks, setWindows }: Props<T>) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const hasInitializedRef = useRef(false);
  const { fetchTaskWindowData, fetchListForWorkflow } = useWindowDataLoader();
  const { showToast } = useToast();

  const initializePinnedWindows = useCallback(
    async (pinnedWindows: TabWindowState[], activeTasks: Task[]) => {
      const failedWindows: { id: string; title: string; reason: string }[] = [];

      const windowResults = await Promise.allSettled(
        pinnedWindows.map(async (pinnedState) => {
          try {
            const taskIds = pinnedState.taskIds || [];

            if (taskIds.length === 0) {
              throw new Error("No task IDs found");
            }

            //Multi-task window
            if (
              taskIds.length > 1 ||
              pinnedState.id.startsWith(ALL_TASKS_PREFIX) ||
              pinnedState.id.startsWith(ALL_TASKS_BRAND_PREFIX)
            ) {
              const tasksToFetch = activeTasks.filter((t) => taskIds.includes(t.id));

              if (tasksToFetch.length === 0) {
                throw new Error("No active tasks found");
              }

              return buildWindowData<T>(
                tasksToFetch,
                pinnedState,
                (tasks) => fetchTaskWindowData(tasks),
                (workflowIds) => fetchListForWorkflow(workflowIds)
              );
            }

            //Single task window
            const taskId = taskIds[0];
            const task = activeTasks.find((t) => t.id === taskId);

            if (!task) {
              throw new Error("Task not found or inactive");
            }

            return buildWindowData<T>(
              [task],
              pinnedState,
              async (tasks) => fetchTaskWindowData(tasks),
              async (workflowIds) => fetchListForWorkflow(workflowIds)
            );
          } catch (error) {
            failedWindows.push({
              id: pinnedState.id,
              title: pinnedState.title,
              reason: error instanceof Error ? error.message : "Unknown error",
            });
            tabWindowStorage.removePinnedWindow(pinnedState.id);
            return null;
          }
        })
      );

      const successfulWindows: TabWindow<T>[] = [];

      for (const result of windowResults) {
        if (result.status === "fulfilled" && result.value !== null) {
          successfulWindows.push(result.value);
        }
      }

      return { successfulWindows, failedWindows };
    },
    [fetchTaskWindowData, fetchListForWorkflow]
  );

  useEffect(() => {
    if (!tasks || hasInitializedRef.current || tasks.length === 0) return;

    const pinnedWindows = tabWindowStorage.getPinnedWindows();
    if (pinnedWindows.length === 0) {
      hasInitializedRef.current = true;
      return;
    }

    const activeTasks = tasks.filter((t) => t.active);
    if (activeTasks.length === 0) {
      hasInitializedRef.current = true;
      return;
    }

    setIsInitializing(true);
    hasInitializedRef.current = true;

    initializePinnedWindows(pinnedWindows, activeTasks)
      .then(({ successfulWindows, failedWindows }) => {
        setWindows(successfulWindows);

        if (failedWindows.length > 0) {
          const failedTitles = failedWindows.map((w) => `"${w.title}"`).join(", ");
          showToast(
            "error",
            "Failed to restore pinned windows",
            `Could not restore: ${failedTitles}`
          );
        }

        setIsInitializing(false);
      })
      .catch((error) => {
        console.error("Failed to initialize pinned windows:", error);
        setIsInitializing(false);
      });
  }, [tasks, initializePinnedWindows, showToast, setWindows]);

  return { isInitializing };
};
